/**
 * projectIntentRichness — shared, side-effect-free predicates used by the
 * three layered guards against the "thin default clobbers a full intent on
 * rerun" data-loss bug (wf wq07loep2).
 *
 * Background
 * ----------
 * The Rerun wizard seeds its `projectIntent` state and then, on submit,
 * write-backs to `site.projectIntent`. A regression chain let a HARDCODED
 * THIN default (`SCR_PROJECT_INTENT_DEFAULTS` — no phases, all the
 * categorical fields at their archetype values) overwrite a full,
 * user-authored intent (e.g. 300 MW exact + a 4-phase rollout) on every
 * rerun. The root cause composed three pieces:
 *   1. a thin hardcoded seed,
 *   2. a strict timestamp gate that dropped the LIVE full intent in favour
 *      of the thin default whenever the report was generated AFTER the last
 *      intent edit (the common case), and
 *   3. an UNCONDITIONAL whole-field write-back.
 *
 * These helpers give the guards a single, well-tested notion of:
 *   - whether an intent carries USER-AUTHORED signal (`isRichProjectIntent`),
 *   - whether an incoming payload is a recognisably SPARSE / DEFAULT one
 *     (`isSparseOrDefaultProjectIntent`),
 *   - and a coarse richness score so a guard can answer "is the incoming
 *     strictly thinner than what we already have?" (`projectIntentRichnessScore`).
 *
 * NONE of these helpers mutate their inputs. They are pure and cheap.
 */

import type { ProjectIntent } from '../../types/siteRag';
import { bucketFromMw, bucketFromInServiceDate } from '../projectIntent';

/**
 * The categorical "archetype" values that the rerun wizard's hardcoded
 * `SCR_PROJECT_INTENT_DEFAULTS` ships. A field sitting at one of these
 * values carries NO user signal on its own — it is indistinguishable from
 * the default the wizard would have synthesised. (We DON'T treat these as
 * user-authored; we treat anything OTHER than these — and anything not
 * 'undecided' — as a positive authoring signal.)
 */
const DEFAULT_MARKER_VALUES: Partial<Record<keyof ProjectIntent, string>> = {
  workloadProfile: 'ai-training',
  powerRequirement: '50-200mw',
  coolingStrategy: 'undecided',
  nuclearInterest: 'considering',
  timeline: '2-5yr',
  priority: 'resilience',
};

/** `fieldSource` provenance tiers that mean "the system synthesised this,
 *  the user did NOT author it". Mirrors `IntentSuggestionSource`. */
const NON_AUTHORED_SOURCES = new Set<string>([
  'fallback',
  'default',
  'archetype-default',
  'dam-default',
]);

/** True when `intent` carries at least one non-empty phase. */
export function hasNonEmptyPhases(intent: ProjectIntent | undefined | null): boolean {
  return Boolean(intent?.phases && intent.phases.length > 0);
}

/**
 * True when `intent` carries a positive user-authoring signal — i.e. it is
 * NOT just the thin archetype default. Signals (any one suffices):
 *   - a non-empty `phases` array,
 *   - an exact `powerRequirementMW`,
 *   - an exact `inServiceDate`,
 *   - any categorical field set to a value OTHER than 'undecided' AND other
 *     than its default-marker value,
 *   - any of the expanded optional fields populated (siteSponsor, reactor
 *     class, power-stack basis, cooling generation/heat-rejection,
 *     custom weights),
 *   - a `fieldSource` entry whose tier is NOT one of the non-authored
 *     synthetic tiers.
 */
export function isRichProjectIntent(intent: ProjectIntent | undefined | null): boolean {
  if (!intent) return false;

  if (hasNonEmptyPhases(intent)) return true;

  if (typeof intent.powerRequirementMW === 'number' && Number.isFinite(intent.powerRequirementMW)) {
    return true;
  }
  if (typeof intent.inServiceDate === 'string' && intent.inServiceDate.trim().length > 0) {
    return true;
  }

  // Categorical fields: any value that is neither 'undecided' nor the thin
  // default marker is a positive authoring signal.
  const categorical: Array<[keyof ProjectIntent, string | undefined]> = [
    ['workloadProfile', intent.workloadProfile],
    ['powerRequirement', intent.powerRequirement],
    ['coolingStrategy', intent.coolingStrategy],
    ['nuclearInterest', intent.nuclearInterest],
    ['timeline', intent.timeline],
    ['priority', intent.priority],
  ];
  for (const [key, value] of categorical) {
    if (value === undefined) continue;
    if (value === 'undecided') continue;
    if (DEFAULT_MARKER_VALUES[key] === value) continue;
    return true;
  }

  // Expanded optional fields — any populated one is user signal.
  if (intent.powerBasis !== undefined) return true;
  if (typeof intent.pueValue === 'number') return true;
  if (typeof intent.powerFactor === 'number') return true;
  if (intent.bopHeatRejection !== undefined) return true;
  if (intent.coolingGeneration !== undefined) return true;
  if (intent.reactorDesignClass !== undefined) return true;
  if (intent.reactorVendorId !== undefined) return true;
  if (intent.siteSponsor !== undefined) return true;
  if (intent.customWeights !== undefined) return true;

  // fieldSource provenance: an authored tier is user signal; purely
  // synthetic tiers are not.
  if (intent.fieldSource) {
    for (const tier of Object.values(intent.fieldSource)) {
      if (typeof tier === 'string' && !NON_AUTHORED_SOURCES.has(tier)) return true;
    }
  }

  return false;
}

/**
 * True when `intent` is a recognisably SPARSE / DEFAULT payload — the kind
 * the rerun wizard synthesises when it has no live/snapshot signal:
 *   - no non-empty phases, AND
 *   - no user-authoring signal (`!isRichProjectIntent`), AND
 *   - EITHER its `fieldSource` is absent / entirely non-authored tiers,
 *     OR it carries no `fieldSource` at all.
 *
 * This is intentionally the logical complement of "rich" for the fields
 * that matter, plus the provenance check, so a guard can phrase the
 * decision as "incoming is sparse-default AND existing is rich → don't
 * clobber".
 */
export function isSparseOrDefaultProjectIntent(
  intent: ProjectIntent | undefined | null,
): boolean {
  if (!intent) return true;
  if (isRichProjectIntent(intent)) return false;
  // Not rich. If it declares an authored provenance tier we'd have caught
  // it in isRichProjectIntent, so reaching here means provenance (if any)
  // is purely synthetic. Treat as sparse/default.
  return true;
}

/**
 * Coarse richness score — higher means "more user-authored content". Used
 * only for the relative "is the incoming strictly thinner?" comparison in
 * the store guard; the absolute magnitude is not meaningful.
 */
export function projectIntentRichnessScore(
  intent: ProjectIntent | undefined | null,
): number {
  if (!intent) return 0;
  let score = 0;

  if (intent.phases && intent.phases.length > 0) score += intent.phases.length * 5;
  if (typeof intent.powerRequirementMW === 'number' && Number.isFinite(intent.powerRequirementMW)) {
    score += 3;
  }
  if (typeof intent.inServiceDate === 'string' && intent.inServiceDate.trim().length > 0) {
    score += 2;
  }

  const categorical: Array<[keyof ProjectIntent, string | undefined]> = [
    ['workloadProfile', intent.workloadProfile],
    ['powerRequirement', intent.powerRequirement],
    ['coolingStrategy', intent.coolingStrategy],
    ['nuclearInterest', intent.nuclearInterest],
    ['timeline', intent.timeline],
    ['priority', intent.priority],
  ];
  for (const [key, value] of categorical) {
    if (value === undefined || value === 'undecided') continue;
    // A non-default categorical value counts for more than one parked at
    // its thin-default marker.
    score += DEFAULT_MARKER_VALUES[key] === value ? 0 : 1;
  }

  if (intent.powerBasis !== undefined) score += 1;
  if (typeof intent.pueValue === 'number') score += 1;
  if (typeof intent.powerFactor === 'number') score += 1;
  if (intent.bopHeatRejection !== undefined) score += 1;
  if (intent.coolingGeneration !== undefined) score += 1;
  if (intent.reactorDesignClass !== undefined) score += 1;
  if (intent.reactorVendorId !== undefined) score += 1;
  if (intent.siteSponsor !== undefined) score += 1;
  if (intent.customWeights !== undefined) score += 1;

  if (intent.fieldSource) {
    for (const tier of Object.values(intent.fieldSource)) {
      if (typeof tier === 'string' && !NON_AUTHORED_SOURCES.has(tier)) score += 1;
    }
  }

  return score;
}

/**
 * Merge a SPARSE incoming intent ONTO a richer existing one: the incoming
 * value is taken only where the existing field is empty / 'undecided' /
 * absent. The existing rich content (phases, exact MW, authored
 * categoricals, expanded fields) is preserved. Used by the store guard when
 * it decides to MERGE rather than REPLACE.
 *
 * This is deliberately conservative — it never DROPS existing rich content
 * and never overwrites a non-default existing categorical with a default
 * incoming one.
 */
export function mergeSparseIntentOntoRich(
  existing: ProjectIntent,
  incoming: ProjectIntent,
): ProjectIntent {
  const merged: ProjectIntent = { ...existing };

  const isEmptyCategorical = (v: string | undefined): boolean =>
    v === undefined || v === 'undecided';

  // Categorical fields: fill only where existing is empty/undecided.
  if (isEmptyCategorical(existing.workloadProfile) && incoming.workloadProfile !== undefined) {
    merged.workloadProfile = incoming.workloadProfile;
  }
  if (isEmptyCategorical(existing.powerRequirement) && incoming.powerRequirement !== undefined) {
    merged.powerRequirement = incoming.powerRequirement;
  }
  if (isEmptyCategorical(existing.coolingStrategy) && incoming.coolingStrategy !== undefined) {
    merged.coolingStrategy = incoming.coolingStrategy;
  }
  if (isEmptyCategorical(existing.nuclearInterest) && incoming.nuclearInterest !== undefined) {
    merged.nuclearInterest = incoming.nuclearInterest;
  }
  if (isEmptyCategorical(existing.timeline) && incoming.timeline !== undefined) {
    merged.timeline = incoming.timeline;
  }
  if (isEmptyCategorical(existing.priority) && incoming.priority !== undefined) {
    merged.priority = incoming.priority;
  }

  // Exact values: fill only where absent.
  if (existing.powerRequirementMW === undefined && incoming.powerRequirementMW !== undefined) {
    merged.powerRequirementMW = incoming.powerRequirementMW;
  }
  if (existing.inServiceDate === undefined && incoming.inServiceDate !== undefined) {
    merged.inServiceDate = incoming.inServiceDate;
  }

  // Phases: keep existing if non-empty; otherwise take incoming.
  if (!hasNonEmptyPhases(existing) && hasNonEmptyPhases(incoming)) {
    merged.phases = incoming.phases;
  }

  return merged;
}

/**
 * The CORRECT clobber-guard merge (wf wq07loep2 follow-up): "incoming wins
 * where authored; existing fills the gaps it dropped".
 *
 * Why this REPLACES the scalar `projectIntentRichnessScore` gate
 * --------------------------------------------------------------
 * The score is a flat ADDITIVE sum (phases×5, MW +3, date +2, each
 * categorical / expanded field +1). That is NOT a partial order, so a
 * thin-but-wide incoming payload can TIE or even OUTSCORE a structurally
 * richer existing one and trigger a whole-field REPLACE that silently drops
 * the existing's phases + exact MW. Proven counterexamples:
 *   - SCORE TIE:   existing {1 phase + 300 MW} (8) vs incoming {8 cheap
 *                  categoricals, no phases/MW} (8) → 8 < 8 is false →
 *                  REPLACE → phases + MW WIPED.
 *   - CHEAP WINS:  existing {2 phases + 220 MW} (13) vs incoming {6
 *                  categoricals + 7 expanded, no phases/MW} (≥13) →
 *                  REPLACE → phases + MW dropped.
 *
 * Instead this runs a PER-FIELD merge UNCONDITIONALLY whenever an existing
 * intent is present (no score gate at all). For every field:
 *   - take the INCOMING value IF incoming carries an authored / non-empty /
 *     non-'undecided' value for it (this honours genuine user edits —
 *     changing a categorical, adding/replacing phases or MW or a date), ELSE
 *   - keep the EXISTING value (incoming omitted it, or it is empty `[]` /
 *     `null` / `undefined` / `'undecided'` / a known thin-default marker) —
 *     so a write can NEVER drop structured content the existing already had.
 *
 * Buckets stay consistent with whichever exact value wins:
 *   - `powerRequirement` is re-derived from the winning `powerRequirementMW`
 *     when one is present; otherwise the categorical-bucket merge applies.
 *   - `timeline` is re-derived from the winning `inServiceDate` when present;
 *     otherwise the categorical-bucket merge applies.
 *
 * Pure — never mutates its inputs. First-time set (no existing) is handled by
 * the caller writing `incoming` verbatim; this helper assumes `existing` is
 * present.
 */
export function mergeRespectingIncoming(
  existing: ProjectIntent,
  incoming: ProjectIntent,
): ProjectIntent {
  const merged: ProjectIntent = { ...existing };

  // ── Categorical fields ──
  // A categorical value is an empty GAP when it is undefined or 'undecided'.
  const isCategoricalGap = (v: string | undefined): boolean =>
    v === undefined || v === 'undecided';

  // Incoming wins for a categorical when EITHER:
  //   (a) incoming carries a genuinely AUTHORED value — defined, not
  //       'undecided', and not the thin-default marker for that field
  //       (e.g. coolingStrategy:'air' over an existing 'liquid'); this honours
  //       real user edits and never lets a marker overwrite authored content; OR
  //   (b) the EXISTING value is itself an empty gap (undefined / 'undecided')
  //       and incoming supplies ANY non-gap value (even a default marker) —
  //       a marker is still better than nothing, so it fills the gap.
  // `powerRequirement` and `timeline` are handled separately below so they
  // stay consistent with the winning exact MW / date.
  const incomingWinsCategorical = (
    key: keyof ProjectIntent,
    existingValue: string | undefined,
    incomingValue: string | undefined,
  ): boolean => {
    if (incomingValue === undefined || incomingValue === 'undecided') return false;
    // (a) genuinely authored incoming.
    if (DEFAULT_MARKER_VALUES[key] !== incomingValue) return true;
    // (b) marker incoming, but only to fill an existing gap.
    return isCategoricalGap(existingValue);
  };

  if (incomingWinsCategorical('workloadProfile', existing.workloadProfile, incoming.workloadProfile)) {
    merged.workloadProfile = incoming.workloadProfile;
  }
  if (incomingWinsCategorical('coolingStrategy', existing.coolingStrategy, incoming.coolingStrategy)) {
    merged.coolingStrategy = incoming.coolingStrategy;
  }
  if (incomingWinsCategorical('nuclearInterest', existing.nuclearInterest, incoming.nuclearInterest)) {
    merged.nuclearInterest = incoming.nuclearInterest;
  }
  if (incomingWinsCategorical('priority', existing.priority, incoming.priority)) {
    merged.priority = incoming.priority;
  }

  // ── Exact power (MW) + its derived bucket ──
  // Incoming MW wins iff finite; the bucket follows the winning MW. If
  // neither side has an exact MW, fall back to the categorical-bucket merge.
  const incomingMwAuthored =
    typeof incoming.powerRequirementMW === 'number'
    && Number.isFinite(incoming.powerRequirementMW);
  const existingMwAuthored =
    typeof existing.powerRequirementMW === 'number'
    && Number.isFinite(existing.powerRequirementMW);
  if (incomingMwAuthored) {
    // A genuine MW edit — derive its bucket so the two never disagree.
    merged.powerRequirementMW = incoming.powerRequirementMW;
    merged.powerRequirement = bucketFromMw(incoming.powerRequirementMW);
  } else if (existingMwAuthored) {
    // Keep the existing exact MW + its already-consistent stored bucket.
    merged.powerRequirementMW = existing.powerRequirementMW;
    merged.powerRequirement = existing.powerRequirement;
  } else if (incomingWinsCategorical('powerRequirement', existing.powerRequirement, incoming.powerRequirement)) {
    merged.powerRequirement = incoming.powerRequirement;
  }

  // ── Exact in-service date + its derived timeline bucket ──
  const incomingDateAuthored =
    typeof incoming.inServiceDate === 'string'
    && incoming.inServiceDate.trim().length > 0;
  const existingDateAuthored =
    typeof existing.inServiceDate === 'string'
    && existing.inServiceDate.trim().length > 0;
  if (incomingDateAuthored) {
    // A genuine date edit — derive its timeline bucket so they never disagree.
    merged.inServiceDate = incoming.inServiceDate;
    merged.timeline = bucketFromInServiceDate(incoming.inServiceDate);
  } else if (existingDateAuthored) {
    // Keep the existing exact date + its already-consistent stored bucket.
    // (Re-deriving here would be non-deterministic — the bucket depends on
    // `now`, so a stored 'over-5yr' could silently drift to '2-5yr' as time
    // passes.)
    merged.inServiceDate = existing.inServiceDate;
    merged.timeline = existing.timeline;
  } else if (incomingWinsCategorical('timeline', existing.timeline, incoming.timeline)) {
    merged.timeline = incoming.timeline;
  }

  // ── Phases ──
  // Incoming wins iff it carries a NON-EMPTY array (an empty/absent array is
  // "not authored" — never let it drop the existing phased rollout).
  if (hasNonEmptyPhases(incoming)) {
    merged.phases = incoming.phases;
  }

  // ── Expanded / optional objects + scalars ──
  // Incoming wins iff it carries a non-null/undefined value; otherwise the
  // existing value is preserved. These are all genuine user content, so a
  // write that omits them must never drop what the existing had.
  const optionalKeys: Array<keyof ProjectIntent> = [
    'powerBasis',
    'pueValue',
    'powerFactor',
    'bopHeatRejection',
    'coolingGeneration',
    'reactorDesignClass',
    'reactorVendorId',
    'siteSponsor',
    'counterparty',
    'geographicConstraints',
    'carbonCommitment',
    'carbonCommitmentNote',
    'datacenterTierTarget',
    'redundancyClass',
    'pueTarget',
    'modularReplicableFlag',
    'laborPoolPreference',
    'decommissioningPlanProvided',
    'interconnectionStatus',
    'fuelAccess',
    'waterSource',
    'landControl',
    'offtake',
    'capacityCreditIntent',
    'wasteHeatOfftake',
    'customWeights',
  ];
  for (const key of optionalKeys) {
    const incomingValue = incoming[key];
    if (incomingValue !== undefined && incomingValue !== null) {
      // 'undecided' is the no-signal sentinel for the string-enum optionals
      // (landControl); never let it overwrite an existing authored value.
      if (incomingValue === 'undecided') continue;
      (merged as Record<keyof ProjectIntent, unknown>)[key] = incomingValue;
    }
  }

  // ── Attestations ──
  // Per-key merge: an incoming attestation value wins where present (incl.
  // 'no' / 'yes'); 'unknown' is the open-question sentinel and never drops an
  // existing authored state. Existing keys the incoming omits are preserved.
  if (existing.attestations || incoming.attestations) {
    const mergedAttestations: NonNullable<ProjectIntent['attestations']> = {
      ...(existing.attestations ?? {}),
    };
    if (incoming.attestations) {
      for (const [key, value] of Object.entries(incoming.attestations)) {
        if (value !== undefined && value !== 'unknown') {
          mergedAttestations[key] = value;
        }
      }
    }
    merged.attestations = mergedAttestations;
  }

  // ── Provenance (fieldSource) ──
  // Shallow-merge so per-field provenance from both sides is retained, with
  // incoming taking precedence on conflicts (it reflects the latest edit).
  if (existing.fieldSource || incoming.fieldSource) {
    merged.fieldSource = {
      ...(existing.fieldSource ?? {}),
      ...(incoming.fieldSource ?? {}),
    };
  }

  return merged;
}
